<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-10 col-lg-8">

        <div class="page-bar py-3">
          <div class="d-flex align-items-center justify-content-between">
            <BackButton class="btn btn-link p-0 mr-2 ml-2" :fallback="fallbackRoute">
              <i class="fas fa-chevron-left"></i>
            </BackButton>
            <div class="page-title flex-grow-1">
              Common Connections Invited
              <div class="page-subtitle" v-if="title">
                Tap a connection to view their events.
              </div>
            </div>

            <span class="badge badge-primary mr-2 text-small" v-if="connections.length">
              {{ connections.length }} total
            </span>
          </div>
        </div>

        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status"></div>
          <div class="mt-2 text-muted small">Loading event...</div>
        </div>

        <div v-else class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded"> 

          <div v-if="showConnectionsSkeleton" class="connections-panel">
            <div class="connection-list" role="list" aria-label="Loading connections">
              <article v-for="n in 3" :key="`skeleton-${n}`" class="connection-item is-skeleton">
                <div class="connection-avatar-wrap">
                  <div class="connection-avatar skeleton-box"></div>
                </div>
                <div class="connection-body">
                  <div class="connection-header">
                    <div class="connection-header-text">
                      <div class="skeleton-box skeleton-line w-60"></div>
                      <div class="skeleton-box skeleton-line w-40 mt-1"></div>
                    </div>
                    <div class="connection-chip skeleton-box w-30"></div>
                  </div>
                  <div class="skeleton-box skeleton-line w-90 mt-2"></div>
                </div>
              </article>
            </div>
          </div>

          <div v-else class="connections-panel">
            <div class="connection-filters" role="toolbar" aria-label="Filter connections">
              <button
                v-for="filter in connectionFilterOptions"
                :key="filter.value"
                type="button"
                class="connection-filter-chip"
                :class="{ active: connectionFilter === filter.value }"
                :aria-pressed="String(connectionFilter === filter.value)"
                @click="connectionFilter = filter.value"
              >
                {{ filter.label }}
              </button>
            </div>

            <div class="connections-panel-body" :class="{ 'is-loading': connectionsLoading }">
              <div class="connections-overlay" v-if="connectionsLoading">
                <div class="loading-bar"></div>
              </div>
              <template v-if="filteredConnections.length">
                <div class="connection-list" role="list">
                  <article
                    v-for="(friend, idx) in filteredConnections"
                    :key="friend.id || friend.email || friend.phone || idx"
                    class="connection-item connection-item--clickable"
                    role="listitem"
                    @click="openConnectionEvents(friend)"
                  >
                    <div class="connection-avatar-wrap">
                      <img
                        v-no-right-click
                        :src="friend.user_img || defaultFriendAvatar"
                        :alt="friend.name || 'Connection'"
                        class="connection-avatar"
                        loading="lazy"
                      >
                    </div>
                    <div class="connection-body">
                      <div class="connection-main">
                        <div class="connection-name">{{ friend.name || 'Connection' }}</div>
                        <div v-if="friend.past_count_msg" class="connection-past-pill">
                          <i class="fas fa-users mr-1"></i>
                          <span>{{ friend.past_count_msg }}</span>
                        </div>
                      </div>
                      <span class="connection-chip" :class="connectionStateModifier(friend)">
                        {{ connectionBadgeLabel(friend) }}
                      </span>
                    </div>
                  </article>
                </div>
              </template>
              <div v-else class="connection-filter-empty" aria-live="polite">
                <img :src="connectionsEmptyImage" alt="No connections for this filter" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="connectionEventsOpen" class="modal-overlay" @click.self="closeConnectionEvents">
    <div class="modal-card connections-modal" role="dialog" aria-modal="true" aria-labelledby="connectionEventsTitle">
      <div class="modal-header">
        <div class="d-flex align-items-center">
          <img
            v-no-right-click
            :src="selectedConnection?.user_img || defaultFriendAvatar"
            alt="Connection avatar"
            class="modal-avatar mr-2"
          >
          <div>
            <h5 id="connectionEventsTitle" class="mb-0">{{ selectedConnection?.name || 'Connection' }}</h5> 
          </div>
        </div>
        <button class="close-btn" @click="closeConnectionEvents" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">
        <div v-if="connectionEventsLoading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status"></div>
          <div class="mt-2 small text-muted">Loading events...</div>
        </div>
        <div v-else-if="connectionEventsError" class="alert alert-warning mb-0">
          {{ connectionEventsError }}
        </div>
        <div v-else-if="connectionEvents.length">
          <router-link
            v-for="event in connectionEvents"
            :key="event.id"
            class="event-card"
            :to="event.eventId ? `/received/${event.eventId}` : undefined"
            @click="event.eventId && closeConnectionEvents()"
          >
            <div class="event-card__media" v-if="event.image">
              <img :src="event.image" alt="Event" class="event-img" loading="lazy">
            </div>
            <div class="event-card__body">
              <div class="event-title">{{ event.title }}</div>
              <div class="event-meta text-muted small">
                <i class="far fa-calendar-alt mr-1"></i>{{ event.dateRange || 'Date N/A' }}
              </div>
              <div class="event-meta text-muted small" v-if="event.location">
                <i class="fas fa-map-marker-alt mr-1"></i>{{ event.location }}
              </div>
              <div class="event-meta text-muted small" v-if="event.host">
                <i class="fas fa-user mr-1"></i>{{ event.host }}
              </div>
              <p v-if="event.description" class="mb-0 text-small text-muted mt-2">{{ event.description }}</p>
            </div>
          </router-link>
        </div>
        <div v-else class="text-center text-muted small py-3">
          No events found for this connection.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import { formatEventDateRange } from '@/utils/date'
import BackButton from '@/components/common/BackButton.vue'

const route = useRoute()
const session = useSessionStore()

const deriveEventId = (r) => {
  const cand = [r.params?.eventid, r.params?.id, r.params?.slug, r.query?.eventid, r.query?.id]
    .find(v => v != null && String(v).trim() !== '')
  if (cand) return String(cand)
  const lastSeg = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
  return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null
}
const eventId = ref(deriveEventId(route))
const info = ref({})
const loading = ref(true)

const connections = ref([])
const connectionsLoading = ref(false)
const connectionFilterOptions = ref([
  { value: '', label: 'All' },
  { value: '1', label: 'Going' },
  { value: '3', label: 'Not Decided' },
  { value: '2', label: 'Not Going' },
  { value: '0', label: 'Invited' }
])
const connectionFilter = ref(connectionFilterOptions.value[0].value)

const connectionEventsOpen = ref(false)
const selectedConnection = ref(null)
const connectionEvents = ref([])
const connectionEventsLoading = ref(false)
const connectionEventsError = ref('')

const defaultFriendAvatar = '/assets/img/av.png'
const defaultEventImage = 'https://dummyimage.com/400x240/e9edf5/7c8697.png&text=Event'
const connectionsEmptyImage = '/assets/icons/no-connections.svg'

const fallbackRoute = computed(() => {
  const slug = route.params?.slug || eventId.value || ''
  return slug ? `/received/${slug}` : '/received'
})

const title = computed(() => info.value.orgname || info.value.eventname || '')

const connectionStatusLabel = (status) => {
  if (Number(status) === 1) return 'Going'
  if (Number(status) === 2) return 'Not going'
  if (Number(status) === 3) return 'Not Decided'
  return 'Invited'
}

const connectionBadgeLabel = (friend) =>
  Number(friend?.rsvp_status) === 1 ? connectionStatusLabel(friend?.status) : 'Invited'

const connectionState = (friend) => {
  if (Number(friend?.rsvp_status) !== 1) return 'pending'
  if (Number(friend?.status) === 1) return 'going'
  if (Number(friend?.status) === 2) return 'notgoing'
  if (Number(friend?.status) === 3) return 'maybe'
  return 'pending'
}

const connectionStateModifier = (friend) => `is-${connectionState(friend)}`

const connectionMetaLine = (friend) => {
  const responded = Number(friend?.rsvp_status) === 1
  const base = responded ? `RSVP: ${connectionStatusLabel(friend?.status)}` : 'Invited · Waiting response'
  const extras = []

  if (friend?.mutual_friends) {
    extras.push(`Mutual: ${friend.mutual_friends} ${friend.mutual_friends === 1 ? 'friend' : 'friends'}`)
  } else if (friend?.mutual_events) {
    extras.push(`Events: ${friend.mutual_events}`)
  }

  return extras.length ? `${base} • ${extras.join(' • ')}` : base
}

const connectionDetailLine = (friend) => {
  if (!friend) return ''
  if (friend.response_note) return `Responded: "${friend.response_note}"`
  if (friend.note) return friend.note
  if (friend.last_seen) return friend.last_seen
  if (friend.source) return `Connected via ${friend.source}`
  return ''
}

const filteredConnections = computed(() => {
  if (!connectionFilter.value && connectionFilter.value !== 0) return connections.value
  const map = { '1': 'going', '2': 'notgoing', '3': 'maybe', '0': 'pending' }
  const mapped = map[String(connectionFilter.value)] || null
  if (!mapped) return connections.value
  return connections.value.filter(friend => connectionState(friend) === mapped)
})
const showConnectionsSkeleton = computed(() => connectionsLoading.value && !connections.value.length)

const buildDateTime = (date, time) => {
  if (!date) return null
  const t = time && String(time).trim() ? time : '00:00'
  const d = new Date(`${date}T${t}`)
  return isNaN(d) ? null : d
}

async function loadDetail() {
  loading.value = true
  try {
    const fd = new FormData()
    fd.append('id', session.userId || '')
    fd.append('eventid', eventId.value || '')

    const res = await api.getReceivedInvitationDetail(fd).catch(() => null)
    const ok = res?.data?.status === 'ok' || res?.status === 'ok'
    if (ok) {
      const row = res.data?.list?.[0] || {}
      info.value = row
    } else {
      info.value = {}
    }
  } finally {
    loading.value = false
  }
}

async function loadConnectionFilters() {
  if (!session.userId || !eventId.value) {
    connectionFilterOptions.value = [
      { value: '', label: 'All' },
      { value: '1', label: 'Going' },
      { value: '3', label: 'Not Decided' },
      { value: '2', label: 'Not Going' },
      { value: '0', label: 'Invited' }
    ]
    connectionFilter.value = connectionFilterOptions.value[0].value
    return
  }
  try {
    const fd = new FormData()
    fd.append('userid', String(session.userId || ''))
    fd.append('event_id', String(eventId.value || ''))

    const res = await api.getConnectionsStats(fd).catch(() => null)
    const ok = res?.data?.status === 'ok'
    if (ok) {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      const mapped = rows
        .map(item => ({
          value: item?.value != null ? String(item.value) : '',
          label: item?.name || item?.label || 'All'
        }))
        .filter(opt => opt.label)
      connectionFilterOptions.value = mapped.length ? mapped : connectionFilterOptions.value
    }
  } catch (err) {
    console.error('load connection stats error', err)
  }
  const first = connectionFilterOptions.value[0]?.value ?? ''
  if (connectionFilter.value !== first) {
    connectionFilter.value = first
  }
}

async function loadConnections() {
  if (!session.userId || !eventId.value) {
    connections.value = []
    connectionsLoading.value = false
    return
  }
  connectionsLoading.value = true
  try {
    const fd = new FormData()
    fd.append('userid', String(session.userId || ''))
    fd.append('event_id', String(eventId.value || ''))
    fd.append('status', String(connectionFilter.value ?? ''))

    const res = await api.getConnections(fd).catch(() => null)
    const ok = res?.data?.status === 'ok'
    if (ok) {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      connections.value = rows.map((row, idx) => ({
        id: row.id || row.userid || row.user_id || `guest-${idx}`,
        name: row.name || row.username || row.email || 'Connection',
        username: row.username || row.user_name || row.handle || '',
        email: row.email || '',
        phone: row.phone || '',
        user_img: row.user_img || row.photo || '',
        status: Number(row.status || row.response_status || 0),
        rsvp_status: Number(row.rsvp_status || row.rsvpStatus || 0),
        adult: Number(row.adult || row.adults || 0),
        child: Number(row.child || row.children || 0),
        mutual_friends: Number(row.mutual_friends || row.mutualFriends || row.mutual_count || 0),
        mutual_events: Number(row.mutual_events || row.mutualEvents || row.events_with_you || row.events || 0),
        last_seen: row.last_seen || row.lastSeen || row.last_interaction || '',
        past_count_msg: row.past_count_msg || row.pastCountMsg || '',
        source: row.source || row.connection_source || row.connected_via || '',
        response_note: row.response_note || row.responseNote || row.response_msg || '',
        note: row.note || row.notes || '',
        connection_user_id: row.userid || row.user_id || row.id
      }))
    } else {
      connections.value = []
    }
  } catch (err) {
    console.error('load connections error', err)
    connections.value = []
  } finally {
    connectionsLoading.value = false
  }
}

async function openConnectionEvents(friend) {
  if (!friend) return
  selectedConnection.value = friend
  connectionEvents.value = []
  connectionEventsError.value = ''
  connectionEventsOpen.value = true
  await loadConnectionEvents(friend)
}

async function loadConnectionEvents(friend) {
  const targetUserId = friend?.connection_user_id || friend?.userid || friend?.user_id || friend?.id
  if (!targetUserId) {
    connectionEventsError.value = 'User id unavailable for this connection.'
    return
  }
  connectionEventsLoading.value = true
  try {
    const fd = new FormData()
    fd.append('userid', String(targetUserId))
    if (eventId.value) fd.append('event_id', String(eventId.value))

    const res = await api.getConnectionsEvents(fd).catch(() => null)
    const ok = res?.data?.status === 'ok'
    if (ok) {
        const rows = Array.isArray(res?.data?.data) ? res.data.data : []
        connectionEvents.value = rows.map((row, idx) => {
          return {
            id: row.id || row.event_id || row.eventid || `event-${idx}`,
            eventId: row.eventid || row.event_id || row.id || null,
            title: row.eventname || row.name || row.orgname || 'Event',
            dateRange: formatEventDateRange({
              fromDate: row.fromdate || row.date,
              toDate: row.todate || row.date,
              fromTime: row.fromtime || row.time,
            toTime: row.totime || row.time_to
          }),
          location: row.address || row.location || '',
          host: row.hostname || row.host || '',
          description: row.description || row.details || row.note || '',
          image: row.eventphoto || row.p1_url || row.photo || row.image || row.banner || row.cover || defaultEventImage
        }
      })
    } else {
      connectionEventsError.value = res?.data?.msg || 'Unable to load events.'
    }
  } catch (err) {
    console.error('get connections events error', err)
    connectionEventsError.value = 'Unable to load events.'
  } finally {
    connectionEventsLoading.value = false
  }
}

function closeConnectionEvents() {
  if (connectionEventsLoading.value) return
  connectionEventsOpen.value = false
  connectionEvents.value = []
  selectedConnection.value = null
  connectionEventsError.value = ''
}

onMounted(() => {
  loadDetail()
  loadConnectionFilters()
})
watch(eventId, (val, oldVal) => {
  if (!val || val === oldVal) return
  loadDetail()
  loadConnectionFilters()
  loadConnections()
})
watch(() => route.fullPath, () => (eventId.value = deriveEventId(route)))
watch(() => session.userId, (val, oldVal) => {
  if (!val || val === oldVal) return
  loadConnectionFilters()
  loadConnections()
})
watch(connectionFilter, (val, oldVal) => {
  if (val === oldVal) return
  loadConnections()
}, { immediate: true })
</script>

<style scoped>
.page-bar{
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.page-title{
  font-weight: 600;
  font-size: 1.05rem;
}
.page-subtitle{
  font-size: .85rem;
  color: #6b7280;
}
.section-title{ font-weight: 600; font-size: 1rem; }
.text-small{font-size:.9rem;}

.modal-overlay {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,.45);
  z-index: 2000;
}
.modal-card {
  width: min(520px, 92vw);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.25);
  overflow: hidden;
  display: flex; flex-direction: column;
}
.modal-header, .modal-footer {
  padding: .85rem 1rem;
  border-bottom: 1px solid #f1f3f5;
}
.modal-body {
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}
.close-btn {
  background: transparent; border: 0; font-size: 1.25rem; line-height: 1;
  color: #6c757d; cursor: pointer;
}
.close-btn:hover { color: #000; }
.modal-avatar{
  width:42px;
  height:42px;
  border-radius:50%;
  object-fit:cover;
  border:2px solid #fff;
  box-shadow:0 6px 14px rgba(15,23,42,.15);
}

.connections-panel{
  position:relative;
  padding:.25rem 0;
}
.connections-panel-body{
  position:relative;
}
.connection-filters{
  display:flex;
  flex-wrap:wrap;
  gap:.25rem;
  margin-bottom:.5rem;
  overflow-x:auto;
  padding-bottom:.2rem;
  scrollbar-width: thin;
}
.connection-filter-chip{
  border:1px solid #e3e7f2;
  border-radius:999px;
  background:#fff;
  color:#4b5563;
  font-size:.78rem;
  font-weight:600;
  padding:.25rem .85rem;
  line-height:1.2;
  transition:all .15s ease;
}
.connection-filter-chip.active{
  background:#f1edff;
  color:#5435d6;
  border-color:#8b5cf6;
  box-shadow:0 4px 12px rgba(139,92,246,.25);
}
.connection-filter-empty{
  padding:1rem 0;
  display:flex;
  justify-content:center;
}
.connection-filter-empty img{
  max-width:70%;
  width:140px;
  opacity:.65;
}
.connection-list{
  display:flex;
  flex-direction:column;
  gap:.6rem;
}
.connection-item{
  display:flex;
  align-items:center;
  gap:.75rem;
  padding:1rem 1.1rem;
  border-radius:14px;
  border:1px solid #e6e9f2;
  background:#fff;
  box-shadow:none;
}
.connection-item--clickable{
  cursor:pointer;
  transition: transform .1s ease, box-shadow .1s ease;
}
.connection-item--clickable:hover{
  transform: translateY(-1px);
  box-shadow:0 6px 14px rgba(15,23,42,.1);
}
.connection-item.is-skeleton{
  box-shadow:none;
  background:#fff;
  border-color:#eef1f6;
}
.connection-avatar-wrap{
  position:relative;
  flex-shrink:0;
}
.connection-avatar{
  width:34px;
  height:34px;
  border-radius:50%;
  object-fit:cover;
  background:#f3f4f6;
  border:2px solid #fff;
  box-shadow:none;
}
.connection-status-dot{
  position:absolute;
  width:12px;
  height:12px;
  border-radius:50%;
  border:2px solid #fff;
  right:2px;
  bottom:0;
  background:#e5e7eb;
}
.connection-body{
  flex:1;
  min-width:0;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.75rem;
}
.connection-name{
  font-size:12px;
  font-weight:700;
  color:#111827;
  text-transform:capitalize;
  word-break:break-word;
}
.connection-main{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:.3rem;
}
.connection-rsvp{
  display:flex;
  align-items:center;
  gap:.35rem;
  font-size:14px;
  color:#4b5563;
}
.connection-rsvp-text{
  font-weight:600;
}
.connection-chip{
  border-radius:999px;
  padding:5px 10px;
  font-size:10px;
  font-weight:600;
  text-transform:capitalize;
  background:#f3f4f6;
  color:#0f172a;
  white-space:nowrap;
  align-self:flex-start;
}
.connection-past-pill{
  display:inline-flex;
  align-items:center;
  gap:.25rem;
  padding:0;
  border-radius:0;
  background:transparent;
  color:#4338ca;
  font-weight:400;
  font-size:10px;
}
.connection-meta-dot.is-going,
.connection-status-dot.is-going{
  background:#16a34a;
}
.connection-chip.is-going{
  background:#d1fae5;
  color:#047857;
}
.connection-meta-dot.is-maybe,
.connection-status-dot.is-maybe{
  background:#fbbf24;
}
.connection-chip.is-maybe{
  background:#fef3c7;
  color:#92400e;
}
.connection-meta-dot.is-notgoing,
.connection-status-dot.is-notgoing{
  background:#f87171;
}
.connection-chip.is-notgoing{
  background:#fee2e2;
  color:#b91c1c;
}
.connection-meta-dot.is-pending,
.connection-status-dot.is-pending{
  background:#a78bfa;
}
.connection-chip.is-pending{
  background:#ede9fe;
  color:#5b21b6;
}
.connections-modal{
  width:min(540px, 95vw);
}

.connections-overlay{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  background:rgba(255,255,255,.78);
  pointer-events:none;
  animation: fadeIn .18s ease;
}
.loading-bar{
  width:80%;
  max-width:240px;
  height:6px;
  border-radius:999px;
  background:linear-gradient(90deg, #e5e7eb, #cbd5f5, #e5e7eb);
  background-size:200% 100%;
  animation: loadingBar 1.1s linear infinite;
}

.skeleton-box{
  position:relative;
  overflow:hidden;
  background:#e9edf7;
  border-radius:10px;
}
.skeleton-line{
  height:12px;
}
.skeleton-box::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.7), rgba(255,255,255,0));
  animation: shimmer 1.2s infinite;
}
.w-30{width:30%;}
.w-40{width:40%;}
.w-60{width:60%;}
.w-90{width:90%;}
.mt-1{margin-top:.25rem;}
.mt-2{margin-top:.5rem;}

.event-card{
  border:1px solid #edf1f7;
  border-radius:14px;
  padding:.85rem;
  box-shadow:0 6px 16px rgba(15,23,42,.06);
  background:#fdfdff;
  margin-bottom:.75rem;
}
.event-card__media{
  width:100%;
  overflow:hidden;
  border-radius:12px;
  margin-bottom:.65rem;
}
.event-img{
  width:100%;
  height:180px;
  object-fit:cover;
  display:block;
  border-radius:12px;
}
.event-card__body{
  display:flex;
  flex-direction:column;
  gap:.2rem;
}
.event-title{
  font-weight:700;
  color:#0f172a;
}
.event-meta i{
  color:#6366f1;
}

@keyframes shimmer{
  from{transform:translateX(-100%);}
  to{transform:translateX(100%);}
}
@keyframes loadingBar{
  from{background-position:200% 0;}
  to{background-position:-200% 0;}
}
@keyframes fadeIn{
  from{opacity:0;}
  to{opacity:1;}
}

@media (max-width: 768px){
  .connection-filters{
    flex-wrap:nowrap;
    white-space:nowrap;
  }
}

@media (max-width: 576px){
  .connection-item{
    padding:.9rem .95rem;
    gap:.6rem;
  }
  .connection-avatar{
    width:28px;
    height:28px;
  }
  .connection-name{
    font-size:12px;
  }
  .connection-chip{
    font-size:12px;
    padding:5px 10px;
  }
  .connection-past-pill{
    font-size:10px;
  }
}
</style>
